<!--
   The MIT License

   Copyright (c) 2019- Nordic Institute for Interoperability Solutions (NIIS)
   Copyright (c) 2018 Estonian Information System Authority (RIA),
   Nordic Institute for Interoperability Solutions (NIIS), Population Register Centre (VRK)
   Copyright (c) 2015-2017 Estonian Information System Authority (RIA), Population Register Centre (VRK)

   Permission is hereby granted, free of charge, to any person obtaining a copy
   of this software and associated documentation files (the "Software"), to deal
   in the Software without restriction, including without limitation the rights
   to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
   copies of the Software, and to permit persons to whom the Software is
   furnished to do so, subject to the following conditions:

   The above copyright notice and this permission notice shall be included in
   all copies or substantial portions of the Software.

   THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
   IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
   FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
   AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
   LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
   OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
   THE SOFTWARE.
 -->
<template>
  <xrd-simple-dialog
    :disable-save="!canUpdate"
    title="trustServices.timestampingService.dialog.edit.title"
    save-button-text="action.save"
    cancel-button-text="action.cancel"
    submittable
    :loading="loading"
    @cancel="$emit('cancel')"
    @save="update"
  >
    <template #content>
      <div class="dlg-input-width">
        <v-text-field
          v-model="tasUrl"
          v-bind="tasUrlAttrs"
          variant="outlined"
          data-test="timestamping-service-url-input"
          autofocus
          persistent-hint
          :label="$t('trustServices.timestampingService.url')"
        />
      </div>

      <div class="space-out-bottom dlg-input-width">
        <v-radio-group
          v-model="costType"
          v-bind="costTypeAttrs"
          inline
          class="dlg-row-input"
        >
          <v-radio
            v-for="type in definedCostTypes"
            :key="type"
            :data-test="`timestamping-service-cost-type-radio-${type}`"
            :label="$t(`trustServices.trustService.costType.${type}`)"
            :value="type"
          ></v-radio>
        </v-radio-group>
      </div>

      <div v-if="!certUploadActive">
        <div class="dlg-input-width mb-6">
          <xrd-button
            outlined
            class="mr-3"
            data-test="view-timestamping-service-certificate"
            @click="navigateToTsaDetails()"
          >
            {{ $t('trustServices.viewCertificate') }}
          </xrd-button>
          <xrd-button
            text
            data-test="upload-timestamping-service-certificate"
            @click="certUploadActive = true"
          >
            <v-icon class="xrd-large-button-icon" icon="icon-upload" />
            {{
              $t(
                'trustServices.timestampingService.dialog.edit.uploadCertificate',
              )
            }}
          </xrd-button>
        </div>
      </div>
      <div v-else>
        <div class="dlg-input-width">
          <CertificateFileUpload
            v-model:file="certFile"
            label-key="trustServices.uploadCertificate"
          />
        </div>
      </div>
    </template>
  </xrd-simple-dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { TimestampingService } from '@/openapi-types';
import { RouteName } from '@/global';
import {
  definedCostTypes,
  useTimestampingServicesStore,
} from '@/store/modules/trust-services';
import { useForm } from 'vee-validate';
import { useNotifications } from '@/store/modules/notifications';
import CertificateFileUpload from '@/components/ui/CertificateFileUpload.vue';
import { useI18n } from 'vue-i18n';
import { useFileRef } from '@/util/composables';

const props = defineProps({
  tsaService: {
    type: Object as () => TimestampingService,
    required: true,
  },
});

const emits = defineEmits(['save', 'cancel']);

const { defineField, handleSubmit, meta } = useForm({
  validationSchema: {
    url: 'required|url',
    costType: `required|one_of:${definedCostTypes}`,
  },
  initialValues: {
    url: props.tsaService.url,
    costType: props.tsaService.cost_type,
  },
});
const [tasUrl, tasUrlAttrs] = defineField('url', {
  props: (state) => ({ 'error-messages': state.errors }),
});
const [costType, costTypeAttrs] = defineField('costType', {
  props: (state) => ({ 'error-messages': state.errors }),
});

const { updateTimestampingService } = useTimestampingServicesStore();
const { showSuccess, showError } = useNotifications();
const router = useRouter();
const { t } = useI18n();

const certFile = useFileRef();
const certUploadActive = ref(false);
const loading = ref(false);
const canUpdate = computed(
  () => meta.value.valid && (meta.value.dirty || certFile.value),
);

const update = handleSubmit((values) => {
  loading.value = true;

  updateTimestampingService(
    props.tsaService.id,
    values.url,
    values.costType,
    certFile.value,
  )
    .then(() => {
      showSuccess(t('trustServices.timestampingService.dialog.edit.success'));
      emits('save');
    })
    .catch((error) => showError(error))
    .finally(() => (loading.value = false));
});

function navigateToTsaDetails() {
  router.push({
    name: RouteName.TimestampingServiceCertificateDetails,
    params: {
      timestampingServiceId: '' + props.tsaService.id,
    },
  });
}
</script>
